import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  InputLabel,
  Radio,
} from '@mui/material'
import { Controller, FieldValues } from 'react-hook-form'
import type { Control, UseFormTrigger } from 'react-hook-form'
import { DatePicker } from 'shared/components'
import {
  DateFieldClearButtonCell,
  DateFieldInputRow,
  FieldWrapper,
  LabeledDateFieldGroup,
} from '../ScheduleFields.style'
import {
  fieldCaptionLabelSx,
  radioOptionIndentSx,
} from '../ScheduleFields.utils'
import { TimeZoneSelect } from '../TimeZoneSelect'
import type {
  ScheduleFieldNames,
  ScheduleFieldsProps,
  ScheduleTranslations,
} from '../ScheduleFields.utils'

export type SectionTimeAccessProps<T extends FieldValues> = {
  disabled: boolean
  temporaryValue: string
  label: string
  isActive: boolean
  control: Control<T>
  fieldNames: ScheduleFieldNames
  labels: ScheduleTranslations
  clearLabel: string
  formatLabel: (text: string) => string
  temporaryPickerShared: Record<string, unknown>
  temporaryDatesRevalidate?: () => Promise<void>
  shouldValidateInterdependentFields: boolean
  trigger: UseFormTrigger<T>
  onClearDate: (fieldName: string) => void
  getFieldError: (fieldKey: string) => string | undefined
  embeddedTimeZoneControl?: ScheduleFieldsProps<T>['embeddedTimeZoneControl']
  shouldShowErrors: boolean
  clearErrors: ScheduleFieldsProps<T>['clearErrors']
}

export function SectionTimeAccess<T extends FieldValues>({
  disabled,
  temporaryValue,
  label,
  isActive,
  control,
  fieldNames,
  labels,
  clearLabel,
  formatLabel,
  temporaryPickerShared,
  temporaryDatesRevalidate,
  shouldValidateInterdependentFields,
  trigger,
  onClearDate,
  getFieldError,
  embeddedTimeZoneControl,
  shouldShowErrors,
  clearErrors,
}: SectionTimeAccessProps<T>) {
  return (
    <Box sx={{ width: '100%', mb: 0.5 }}>
      <FormControlLabel
        value={temporaryValue}
        control={<Radio disabled={disabled} />}
        label={label}
      />
      {isActive ? (
        <Box sx={radioOptionIndentSx}>
          <FieldWrapper container>
            {embeddedTimeZoneControl ? (
              <TimeZoneSelect
                control={control}
                fieldNames={fieldNames}
                timeZoneControl={embeddedTimeZoneControl}
                disabled={disabled}
                shouldShowErrors={shouldShowErrors}
                clearErrors={clearErrors}
                getFieldError={getFieldError}
              />
            ) : null}
            <LabeledDateFieldGroup>
              <InputLabel
                shrink
                htmlFor={String(fieldNames.temporaryFromDate)}
                sx={fieldCaptionLabelSx}
              >
                {formatLabel(labels.startAccess)}
              </InputLabel>
              <DateFieldInputRow container>
                <Controller
                  name={fieldNames.temporaryFromDate as any}
                    control={control}
                    render={({ field }) => (
                      <>
                        <Grid item xs={12} md={9}>
                          <DatePicker
                            {...field}
                            disabled={disabled}
                            externalLabel
                            inputId={String(fieldNames.temporaryFromDate)}
                            {...temporaryPickerShared}
                            revalidateFunc={
                              temporaryDatesRevalidate ?? (async () => {})
                            }
                            errorMessage={getFieldError(
                              fieldNames.temporaryFromDate
                            )}
                            onChange={(value: Date | null) => {
                              field.onChange(value)
                              if (shouldValidateInterdependentFields) {
                                void trigger([
                                  fieldNames.temporaryFromDate,
                                  fieldNames.temporaryToDate,
                                ] as any)
                              }
                            }}
                          />
                        </Grid>
                        <DateFieldClearButtonCell item xs={12} md={2}>
                          <Button
                            disabled={disabled || field.value == null}
                            fullWidth={false}
                            onClick={() =>
                              onClearDate(fieldNames.temporaryFromDate)
                            }
                          >
                            {clearLabel}
                          </Button>
                        </DateFieldClearButtonCell>
                      </>
                    )}
                  />
              </DateFieldInputRow>
            </LabeledDateFieldGroup>
            <LabeledDateFieldGroup>
              <InputLabel
                shrink
                htmlFor={String(fieldNames.temporaryToDate)}
                sx={fieldCaptionLabelSx}
              >
                {formatLabel(labels.endAccess)}
              </InputLabel>
              <DateFieldInputRow container>
                <Controller
                  name={fieldNames.temporaryToDate as any}
                  control={control}
                  render={({ field }) => (
                    <>
                      <Grid item xs={12} md={9}>
                        <DatePicker
                          {...field}
                          disabled={disabled}
                          externalLabel
                          inputId={String(fieldNames.temporaryToDate)}
                          {...temporaryPickerShared}
                          revalidateFunc={
                            temporaryDatesRevalidate ?? (async () => {})
                          }
                          errorMessage={getFieldError(
                            fieldNames.temporaryToDate
                          )}
                          onChange={(value: Date | null) => {
                            field.onChange(value)
                            if (shouldValidateInterdependentFields) {
                              void trigger([
                                fieldNames.temporaryFromDate,
                                fieldNames.temporaryToDate,
                              ] as any)
                            }
                          }}
                        />
                      </Grid>
                      <DateFieldClearButtonCell item xs={12} md={2}>
                        <Button
                          disabled={disabled || field.value == null}
                          fullWidth={false}
                          onClick={() =>
                            onClearDate(fieldNames.temporaryToDate)
                          }
                        >
                          {clearLabel}
                        </Button>
                      </DateFieldClearButtonCell>
                    </>
                  )}
                />
              </DateFieldInputRow>
            </LabeledDateFieldGroup>
          </FieldWrapper>
        </Box>
      ) : null}
    </Box>
  )
}
